import type { SyncBlockInstance } from '@atlaskit/editor-synced-block-provider';
import {
	fetchReferences,
	getConfluencePageAri,
	parseResourceId,
} from '@atlaskit/editor-synced-block-provider';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';

import { isSSR } from './utils/isSSR.ts';
import { addToSSREditorNodeDataProvidersStore } from './utils/ssrDataStore.ts';
import { syncBlockContainsNodes } from './utils/syncBlockContainsNodes.ts';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/syncedBlocksData';

// Timeout for fetching synced block references in milliseconds
// This is currently set to higher value to account for potential network latencys
// We will monitor performance and adjust this value as needed
const DEFAULT_TIMEOUT = 1500;

export interface PreloadSyncedBlocksDataOptions {
	/**
	 * The ID of the content to preload synced blocks for
	 */
	contentId: string;

	/**
	 * The cloud ID associated with the content
	 */
	cloudId: string;

	/**
	 * Whether the content is a blog post
	 */
	isBlog: boolean;

	/**
	 * The measure segment identifier for performance tracking
	 */
	measureSegment?: string;

	/**
	 * Timeout in milliseconds for fetching synced block references
	 * @default 1500
	 */
	timeout?: number;
}

/**
 * Preloads synced blocks data and stores it in the window object for hydration.
 *
 * This function handles a special case of editor node (syncBlock) data providers that requires
 * separate preloading due to its nested structure and performance characteristics.
 *
 * **Why separate preloading?**
 *
 * Synced blocks introduce additional network latency that can slow down the main
 * editor initialization if fetched together. By splitting the flows, we achieve
 * better parallelization:
 *
 * Instead of:
 * 1. Fetch main ADF + Synced blocks → Fetch other data providers combined (from both main ADF and Synced blocks)
 *
 * We do:
 * 1. Fetch main ADF → fetch main data providers
 * 2. Fetch synced blocks → fetch synced blocks' nested data providers
 *
 * This parallel execution ensures synced blocks don't block the main editor load,
 * this could help improve overall load times, and allow synced blocks to have a separate
 * timeout without affecting the main editor load.
 * However this means we might have more network requests in total due to less batching opportunities.
 *
 * **Feature flag encapsulation**
 *
 * All feature flag checks and conditional logic for synced blocks are centralized
 * here rather than scattered across multiple files.
 *
 * @param contentId - The ID of the content to preload synced blocks for
 * @param cloudId - The cloud ID associated with the content
 * @param isBlog - Whether the content is a blog post
 * @returns Promise resolving to SyncedBlocksData containing synced block references
 */
export async function preloadSyncedBlocksData(
	options: PreloadSyncedBlocksDataOptions,
): Promise<void> {
	const { contentId, cloudId, isBlog, measureSegment, timeout } = options;
	// Check feature flags before attempting to fetch synced block data
	if (
		!contentId ||
		!cloudId ||
		!isSSR() ||
		typeof isBlog === 'undefined' ||
		!expVal('platform_synced_block', 'isEnabled', false) ||
		!fg('platform_synced_block_dogfooding')
	) {
		return;
	}

	const segmentName = measureSegment || MEASURE_SEGMENT;

	await SSRMeasures.run(segmentName, async () => {
		const syncBlockReferences = await fetchSyncBlockReferences(
			contentId,
			cloudId,
			isBlog,
			segmentName,
			timeout,
		);

		if (syncBlockReferences) {
			addToSSREditorNodeDataProvidersStore({ syncBlocks: syncBlockReferences });
		} else {
			return;
		}

		const tasks: Promise<any>[] = [];

		const syncedBlocksWithMedia = syncBlockReferences.filter((syncBlock) => {
			// Preload media data providers for synced blocks that contain media nodes
			const containedNodeTypes = syncBlockContainsNodes(syncBlock, ['media']);
			return containedNodeTypes.length > 0 && containedNodeTypes.includes('media');
		});

		syncedBlocksWithMedia.forEach((syncBlockInstance) => {
			tasks.push(preloadMediaTokensForSyncBlock(syncBlockInstance));
		});

		await Promise.allSettled(tasks);
	});
}

/**
 * Preload media tokens for synced blocks that contain media
 * Returns a map of resourceId -> media config for SSR
 */
export async function preloadMediaTokensForSyncBlock(
	syncBlock: SyncBlockInstance | undefined,
): Promise<Record<string, any> | null> {
	if (!syncBlock) {
		return null;
	}

	const { product, contentId } = parseResourceId(syncBlock.resourceId) || {};

	if (!product || !contentId) {
		return {
			resourceId: syncBlock.resourceId,
			result: undefined,
		};
	}

	if (product === 'confluence-page') {
		const result = await preloadMediaToken(contentId);

		return {
			resourceId: syncBlock.resourceId,
			result,
		};
	}

	return null;
}

/**
 * Fetches synced block references for a given content.
 * @param contentId - The ID of the content
 * @param cloudId - The cloud ID
 * @param isBlog - Whether the content is a blog post
 * @param timeout - Maximum time in milliseconds to wait (default: 800ms)
 * @returns Promise resolving to synced block instances or undefined on error/timeout
 */
export function fetchSyncBlockReferences(
	contentId: string,
	cloudId: string,
	isBlog: boolean,
	measureSegment: string = MEASURE_SEGMENT,
	timeout: number = DEFAULT_TIMEOUT,
): Promise<SyncBlockInstance[] | undefined> {
	return SSRMeasures.run(`${measureSegment}/fetch:SyncBlockReferences`, async () => {
		const pageAri = getConfluencePageAri({
			cloudId,
			pageId: contentId,
			pageType: isBlog ? 'blogpost' : 'page',
		});

		try {
			const results = await Promise.race([
				fetchReferences(pageAri),
				new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), timeout)),
			]);

			if (!results || typeof results === 'string') {
				// has error return undefined
				return undefined;
			}

			return results as SyncBlockInstance[];
		} catch {
			return undefined;
		}
	});
}
